import { useEffect, useState, useRef } from 'react';
import { useParams } from "react-router-dom";
import useFetch from "../hooks/useFetch";
import { useNavigate } from "react-router-dom";
import FinalResultCardHeader from '../components/FinalResultCardHeader';
import FinalResultCardHeaderTWDShowcase from '../components/FinalResultCardHeaderTWDShowcase';
import FinalResultCard from '../components/FinalResultCard';
import FinalResultCardTWDShowcase from '../components/FinalResultCardTWDShowcase';

import ResultsHeader from '../components/ResultsHeader';
import ResultsCard from '../components/ResultsCard';

import { IllegalMoveFormData } from '../components/IllegalMoveForm';

import { toast } from "react-toastify";

type JudgeResult = {
  judge: string;
  score: string;
  judges_score_place: number;
  technique_score: number;
  execution_score: number;
  user_judge_id: number;
  pointsdeduction_score: number;
  pointsdeduction_comment: string;
};

type FinalResult = {
  id: string | number;
  performance_id: number;
  entry: number;
  performers: string;
  talktape: string;
  program: string;
  program_skills_abhs: number;
  program_skills_abhs_mod2: number;
  program_skills_pom_straps: number;
  routine: string;
  total_scores: number;
  final_scores: number;
  place: number;
  judgescount: number;
  tie_resolved_by: number;
  tb1_with_hl_final_scores: number;
  tb2_technique_final_scores: number;
  tb3_execution_final_scores: number;

  twdsc_tb1_rank_without_HL: number;
  twdsc_tb2_rank_with_HL: number;
  twdsc_tb3_total_scores_without_HL: number;
  twdsc_tb4_total_scores_with_HL: number;
  twdsc_tb5_total_technique: number;
  twdsc_tb6_total_execution: number;

  judgeResults: JudgeResult[];
  judges_score_place: number;
  performance_type: string;
};

type Division = {
  id: number;
  event_id: number;
  title: string;
  start_time: string;
  status: string;
  end_time: string;
  places_to_announce: number;
  event: {
    performance_type: string;
    performance_subtype: string;
  };
  twd_places_to_announce: JSON;
}

interface Result {
  entry_number: number;
  performers: any; // Changed from performerName
  program: string; // Changed from performerName
  routine: string;   // Changed from routineName
  totalscore: number;
  finalscore: number;
  performance_id: number;
  performance_type: string;
  event_id:number;
  user_programdirector_id: number;
  program_skills_abhs: number;
  program_skills_abhs_mod2: number;
  program_skills_pom_straps: number;
  is_editing_allowed: boolean;
  judges: {
    Judge: string;
    Technique: number;
    Execution: number;
    Difficulty: number;
    Choreography: number;
    OverallEffect: number;
    PointsDeduction: number;
    Max_Technique: number;
    Max_Execution: number;
    Max_Difficulty: number;
    Max_Choreography: number;
    Max_OverallEffect: number;
    Max_PointsDeduction: number;
    PointsDeduction_Comment: string;
    user_judge_id: number;
    isHighest: number;
    isLowest: number;
    Total: number;
  }[];
}

const Placement = () => {
  const navigate = useNavigate();
  const params = useParams<{ division_performance_group_id: string }>();
  const division_performance_group_id = params?.division_performance_group_id;
  const currentRole = JSON.parse(localStorage.getItem("user") || "{}").current_role;
  const currentUserID = JSON.parse(localStorage.getItem("user") || "{}").id;
  const [approveButtonLabel, setApproveButtonLabel] = useState("Approve All");
  const [dataFinalResults, setDataFinalResults] = useState<FinalResult[] | undefined>(undefined);
  const [division, setDivision] = useState<Division | undefined>(undefined);
  const [hasCalledFetch, setHasCalledFetch] = useState(false);
  const [hasCalledFetchResults, setHasCalledFetchResults] = useState(false);
  const [hasCalledFetchDivision, setHasCalledFetchDivision] = useState(false);
  const [hasCalledFetchFinalResults, setHasCalledFetchFinalResults] = useState(false);

  //////fetch division and its status///
  const { data: divisionData, isLoading: isDivisionLoading, errorContent: errorContent, fetchData: fetchDivision, hasFetched: divisionFetched } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/divisions_performance_groups/${division_performance_group_id}`,
    method: "GET",
  });
      
  useEffect(() => {
    if (!hasCalledFetchDivision && !divisionFetched) {
      setHasCalledFetchDivision(true); // Mark as called
      fetchDivision();
    } else {
      //console.log(divisionData);
      setDivision(divisionData);
    }
  }, [hasCalledFetchDivision, divisionFetched, fetchDivision, divisionData]);

  //////fetch placements results/////////////
  const { data: results, errorContent: errorContentResults, isLoading: isLoadingResults, fetchData: fetchResultsByDivision, hasFetched: hasResultsFetched } = useFetch({
    //url: `${import.meta.env.VITE_API_URL}/admin/performances_results/${division_performance_group_id}`,
    url: `${import.meta.env.VITE_API_URL}/admin/performances_results_by_user/${division_performance_group_id}/${currentRole}/${currentUserID}`,
    method: "GET",
  });

  // Conditional fetching for placements based on role and division status
  useEffect(() => {
    if (
      !hasCalledFetchResults && // Ensure fetch hasn't been called
      !isDivisionLoading && division && division.status && !hasResultsFetched) {
      const status = division.status;
      if (
        currentRole === 1 || // Admin role can always fetch placements
        currentRole === 8 || // Tabulator role can always fetch placements
        (currentRole === 4 && status === "ready_to_announce") || // Announcer can fetch when results are ready to announce
        (currentRole === 7 ) ||
        ((currentRole === 3 || currentRole === 4 || currentRole === 5 || currentRole === 6 || currentRole === 9) && status === "announced") // Director and Judge (combined) can fetch when results are announced
      ) {
        setHasCalledFetchResults(true); // Mark as called
        fetchResultsByDivision(); // Trigger placements fetch if conditions are met
      }
    }
  }, [hasCalledFetchResults, isDivisionLoading, division, isLoadingResults, hasResultsFetched, currentRole, fetchResultsByDivision]);

  //////final results//////////////
  const { fetchData: fetchFinal_Results, data: final_Results, error: errorContentfinal_Results, isLoading: isLoadingfinal_Results, hasFetched: hasFetchedfinal_Results } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/admin/final_results/${division_performance_group_id}?performance_type=${division?.event?.performance_type ?? null}&performance_subtype=${division?.event?.performance_subtype ?? null}`,
    method: "GET",
  });

  const { data: updatedDivisionData, fetchData: updateDivision, error: errorDivision, hasFetched: hasFetchedDivision  } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/admin/updatedivision_status`,
    method: "POST",
  });

  //approve / disapprove All 
  useEffect(() => {
    if (final_Results && hasFetchedfinal_Results && final_Results.message != "") {
      setApproveButtonLabel("Disapprove All"); // Toggle button label
      setDataFinalResults(final_Results.dataresult);
      
      if ( division?.status === "announced" ) {
        toast.info(final_Results.message);
      }
      final_Results.message="";
    }
  }, [final_Results, hasFetchedfinal_Results, division]);

  useEffect(() => {    
    if ( 
      !hasCalledFetch && // Ensure it hasn't been called already
      !isDivisionLoading && division && division.status && !hasFetchedfinal_Results) {
      if (
        ((currentRole === 1 || currentRole === 8) && division.status !== "judging") ||
        (currentRole === 4 && (division.status === "ready_to_announce" || division.status === "announced")) ||
        (currentRole === 7 && division.status !== "judging") ||
        ((currentRole === 3 || currentRole === 5 || currentRole === 6 || currentRole === 9) && division.status === "announced")
      ) {
        setHasCalledFetch(true); // Mark as called
        //alert(!isDivisionLoading + " - " +  division + " - " + division?.status + " - " + !hasFetchedfinal_Results);    
        fetchFinal_Results();
      }
    }
  }, [hasCalledFetch, final_Results, fetchFinal_Results, errorContentfinal_Results, hasFetchedfinal_Results, isDivisionLoading, division, currentRole]);

  //
  const handleApproveDisapprove = async () => {
    if (approveButtonLabel === "Disapprove All") {
      const userDisapproveConfirmed = window.confirm(
        "Are you sure you want to disapprove results?"
      );
      if ( !userDisapproveConfirmed) {
        return;
      }
      setHasCalledFetchResults(false); // Mark as called
      setHasCalledFetchFinalResults(false); // Mark as called
      await updateDivision({ divisions_performance_groups_id: division_performance_group_id, status: "judging", performance_type: division?.event?.performance_type ?? null,performance_subtype: division?.event?.performance_subtype ?? null });
      
      return; // Add logic to disapprove if needed
    }
    
    if ( division?.event.performance_type === "sds" &&  division?.places_to_announce === 0 ){
      toast.warn("Places to announce not set!");
      return;
    }else if(division?.event.performance_type === "twd" && division?.event.performance_subtype === "showcase" &&  division?.places_to_announce === 0){
      toast.warn("Places to announce not set!");
      return;
    }
    const userConfirmed = window.confirm(
      "Are you sure you want to approve results?"
    );    
    if (!userConfirmed) {
      return; // Exit if the user does not confirm
    }
    try {
      setHasCalledFetchFinalResults(false); // Mark as called
      await updateDivision({ divisions_performance_groups_id: division_performance_group_id, status: "approved", performance_type: division?.event?.performance_type ?? null,performance_subtype: division?.event?.performance_subtype ?? null, });
    } catch (error) {
      console.error('Error approving division:', error);
      //alert('An error occurred while approving entries.');
    }
  };
  
  const handleReadyToAnnounce = async () => {
    try{
      const userReadyToAnnounceConfirmed = window.confirm(
        "Are you sure?"
      );
      if ( !userReadyToAnnounceConfirmed) {
        return;
      }
      setHasCalledFetchFinalResults(false); // Mark as called
      await updateDivision({ divisions_performance_groups_id: division_performance_group_id, status: "ready_to_announce" });

    } catch (error) {
      console.error('Error approving division:', error);
      //alert('An error occurred while approving entries.');
    }
  };

  const handleResultAnnounced = async () => {
  try {
    const userResultAnnouncedConfirmed = window.confirm(
      "Are you sure?"
    );
    if ( !userResultAnnouncedConfirmed) {
      return;
    }
    setHasCalledFetchFinalResults(false); // Mark as called
    await updateDivision({ divisions_performance_groups_id: division_performance_group_id, status: "announced", performance_type: division?.event?.performance_type ?? null,performance_subtype: division?.event?.performance_subtype ?? null });
    } catch (error) {
      console.error('Error approving division:', error);
      alert('An error occurred while approving entries.');
    }
  };

  //Apply Illegal Move Points
  const { data: resultsAfterIllegalMove,  fetchData: fetchResultIllegalMove, errorContent: errorContentIllegalMove, isLoading: isLoadingIllegalMove, hasFetched: hasFetchedIllegalMoveData } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/admin/update_illegalmove`,
    method: "POST",
  });
  const closePopupRef = useRef<() => void>();
  const handleIllegalMoveSubmit = async (formdata: IllegalMoveFormData) => {
      try {
      setHasCalledFetchResults(false); // Mark as called
      await fetchResultIllegalMove({ performance_id: formdata.performance_id, entry_number: formdata.entry_number, category_comment: formdata.comment });
      // Trigger the closePopup function in ResultsCardTWD
      if (closePopupRef.current) {
        closePopupRef.current();
      }
    } catch (error) {
      console.error('Error updating illegal move: ', error);
    }
  };
  useEffect(() => {
    if (!hasCalledFetchResults && !isLoadingIllegalMove && hasFetchedIllegalMoveData && resultsAfterIllegalMove.message != "" ) {
      setHasCalledFetchResults(true); // Mark as called
      fetchResultsByDivision();
      toast.info(resultsAfterIllegalMove?.message);
      resultsAfterIllegalMove.message = "";
    }
  }, [hasFetchedIllegalMoveData, resultsAfterIllegalMove, isLoadingIllegalMove, hasCalledFetchResults, fetchResultsByDivision]);

  //Remove Illegal Move Points
  const { data: resultsAfterIllegalMoveRemove,  fetchData: fetchResultIllegalMoveRemove, errorContent: errorContentIllegalMoveRemoved, isLoading: isLoadingIllegalMoveRemove, hasFetched: hasFetchedIllegalMoveDataRemove } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/admin/remove_illegalmove`,
    method: "POST",
  });
  const handleIllegalMoveRemove = async (formdata: IllegalMoveFormData) => {
      try {
        const userConfirmed = window.confirm(
          "Are you sure you want to remove the Illegal Points for Entry "+formdata.entry_number+"?"
        );    
        if (!userConfirmed) {
          return; // Exit if the user does not confirm
        }
      setHasCalledFetchResults(false); // Mark as called
      await fetchResultIllegalMoveRemove({ performance_id: formdata.performance_id, entry_number: formdata.entry_number });
    } catch (error) {
      console.error('Error removing the illegal move points : ', error);
    }
  };
  useEffect(() => {
    if (!hasCalledFetchResults && !isLoadingIllegalMoveRemove && hasFetchedIllegalMoveDataRemove && resultsAfterIllegalMoveRemove.message != "" ) {
      setHasCalledFetchResults(true); // Mark as called
      fetchResultsByDivision();
      toast.info(resultsAfterIllegalMoveRemove?.message);
      resultsAfterIllegalMoveRemove.message = "";
    }
  }, [hasFetchedIllegalMoveDataRemove, resultsAfterIllegalMoveRemove, isLoadingIllegalMoveRemove, hasCalledFetchResults, fetchResultsByDivision]);
  
  //remove judges scores
  const { data: resultsAfterRemoveJudge,  fetchData: fetchResultRemoveJudge, errorContent: errorContentRemoveJudge, error: errorResponseIllegalMoveRemove, isLoading: isLoadingRemoveJudge, hasFetched: hasFetchedRemoveJudge } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/scores/removejudgescorecard`,
    method: "POST",
  });
  async function removeJudgeScores (event_id:number, performance_id:number, judge_id:number, entry_number: number) {
    try {
      if(confirm("Are you sure")){
        setHasCalledFetchResults(false); 
        await fetchResultRemoveJudge({ event_id: event_id, performance_id: performance_id, judge_id: judge_id, entry_number: entry_number});
      }
    } catch (error) {
      console.error('Error removing judge score: ', error);
    }
  }
  useEffect(() => {
    if ( errorResponseIllegalMoveRemove && !errorResponseIllegalMoveRemove.success && errorResponseIllegalMoveRemove.data && errorResponseIllegalMoveRemove.data.message != "") {
      toast.warn(errorResponseIllegalMoveRemove?.data?.message);
      errorResponseIllegalMoveRemove.data.message="";
    }

    if (!hasCalledFetchResults && !isLoadingRemoveJudge && hasFetchedRemoveJudge && resultsAfterRemoveJudge.message != "") {
      setHasCalledFetchResults(true); // Mark as called
      fetchResultsByDivision();
      toast.info(resultsAfterRemoveJudge?.message);
      resultsAfterRemoveJudge.message="";
    }
  }, [hasFetchedRemoveJudge, resultsAfterRemoveJudge, isLoadingRemoveJudge, hasCalledFetchResults, fetchResultsByDivision, errorResponseIllegalMoveRemove]);
  
  //update perforamnce status to approve or disapprove
  const { data: resultsAfterPerformanceAction,  fetchData: fetchResultPerformanceAction, errorContent: errorContentPerformanceAction, isLoading: isLoadingPerformanceAction, hasFetched: hasFetchedPerformanceAction } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/admin/update_performance`,
    method: "POST",
  });
  async function performanceAction (performance_id:number, entry_number:number, is_editing_allowed:boolean, judges_count:number) {
    try {
      if (judges_count === 3 || judges_count === 5) { 
        if(confirm("Are you sure")){
          setHasCalledFetchResults(false); 
          await fetchResultPerformanceAction({ performance_id: performance_id, entry_number: entry_number, is_editing_allowed: is_editing_allowed});
        }
      } else {
        toast.warn("Invalid judge count! Action can only be performed when there are 3 or 5 judges.");
      }
    } catch (error) {
      console.error('Error updating perforamnce: ', error);
    }
  }
  useEffect(() => {
    if (!hasCalledFetchResults && !isLoadingPerformanceAction && hasFetchedPerformanceAction && resultsAfterPerformanceAction.message != "" ) {
      setHasCalledFetchResults(true); // Mark as called
      fetchResultsByDivision();
      toast.info(resultsAfterPerformanceAction?.message);
      resultsAfterPerformanceAction.message = "";
    }
  }, [hasFetchedPerformanceAction, resultsAfterPerformanceAction, isLoadingPerformanceAction, hasCalledFetchResults, fetchResultsByDivision]);

  //update division status and toast warn
  useEffect(() => {
    if (hasFetchedDivision ) {
      if (errorDivision && !errorDivision?.success) {
          errorDivision.success=true;
          toast.warn(
            <>
              <div style={{
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}>
              {errorDivision?.data?.message}
                {"\n"}{"\n"}
                {errorDivision?.data?.stack}
              </div>
            </>, {
              autoClose: 8000,
            });
      }
      //console.log("updatedDivisionData --- ",updatedDivisionData, updatedDivisionData?.success);
      if ( updatedDivisionData?.success ) {
        //alert(divisionAfterPlacestoannounce.division.places_to_announce);
        setDivision(updatedDivisionData.division);
        if ( updatedDivisionData?.division?.status === "judging" ) {
          if ( !hasCalledFetchResults) {
            setHasCalledFetchResults(true); // Mark as called
            fetchResultsByDivision(); // Trigger placements fetch if conditions are met
          }
          setApproveButtonLabel("Approve All");
        } else {
          setApproveButtonLabel("Disapprove All");
        }

        if (!hasCalledFetchFinalResults && updatedDivisionData.message != "") {
          if ( updatedDivisionData?.division?.status !== "judging" ) {
            fetchFinal_Results();
          }
          toast.info(updatedDivisionData?.message);
          setHasCalledFetchFinalResults(true); // Mark as called
          updatedDivisionData.message="";
        }
      }
    }
  }, [hasFetchedDivision, updatedDivisionData, errorDivision, fetchResultsByDivision, hasCalledFetchFinalResults, fetchFinal_Results, hasCalledFetchResults]);

    //set places to announce
    const { data: divisionAfterPlacestoannounce,  fetchData: fetchDivisionAfterPlacestoannounce, errorContent: errorContentPlacestoannounce, error: errorResponsePlacestoannounce, isLoading: isLoadingPlacestoannounce, hasFetched: hasFetchedPlacestoannounce } = useFetch({
      url: `${import.meta.env.VITE_API_URL}/admin/update_placestoannounce`,
      method: "POST",
    });
    async function handlePlacesToAnnounce () {
      try {
        const userPlacestoannounce = window.prompt(
          "Total Performances in Division: "+results.results.length+" \nEnter the number of awards to be announced. Ex: '12' means 1st-12th"
        );
        if (userPlacestoannounce) {
          const parsedPlaces = Number(userPlacestoannounce);
        
          if (isNaN(parsedPlaces)) {
            toast.warn("Invalid input! Places to announce must be a number.");
          } else if (parsedPlaces > results.results.length) {
            toast.warn("Places to announce cannot exceed the number of entries!");
          } else {
            await fetchDivisionAfterPlacestoannounce({
              division_id: divisionData.id,
              places_to_announce: parsedPlaces,
            });
          }
        }
      } catch (error) {
        console.error('Error updating places to announce: ', error);
      }
    }
    useEffect(() => {
      if ( hasFetchedPlacestoannounce) {
        if ( errorResponsePlacestoannounce && !errorResponsePlacestoannounce.success && errorResponsePlacestoannounce.data && errorResponsePlacestoannounce.data.message != "") {
          toast.warn(errorResponsePlacestoannounce?.data?.message);
          errorResponsePlacestoannounce.data.message="";
        }  
        if (divisionAfterPlacestoannounce?.success) {
          if ( !isLoadingPlacestoannounce && (updatedDivisionData?.division?.status == "judging" || !updatedDivisionData?.division?.status)) {
            setDivision(divisionAfterPlacestoannounce.division);
          }
          
          if (divisionAfterPlacestoannounce.message != "" ) {
            toast.info(divisionAfterPlacestoannounce?.message);
            divisionAfterPlacestoannounce.message = "";
          }
        }
      }
    }, [divisionAfterPlacestoannounce, fetchDivisionAfterPlacestoannounce, fetchDivision, errorContentPlacestoannounce, errorResponsePlacestoannounce, isLoadingPlacestoannounce, hasFetchedPlacestoannounce, hasFetchedDivision, updatedDivisionData]);

  const parsedDivisionId = division_performance_group_id
  ? parseInt(division_performance_group_id, 10)
  : undefined;

  if (parsedDivisionId === undefined) {
    console.error('Invalid division_performance_group_id');
    return null; // or render an appropriate fallback
  }

  if (errorContent || errorContentResults || errorContentfinal_Results || errorContentIllegalMove || errorContentRemoveJudge || errorContentPerformanceAction || errorContentIllegalMoveRemoved || errorContentPlacestoannounce) {
    const content = errorContent || errorContentResults || errorContentfinal_Results || errorContentIllegalMove || errorContentRemoveJudge || errorContentPerformanceAction || errorContentIllegalMoveRemoved || errorContentPlacestoannounce;
    return (
      <div className="error-container">
        <p>
          {typeof content === 'string' ? (
            content
          ) : (
            <>
              ⚠️ Session expired. Please <a href="/login">login</a> again.
            </>
          )}
        </p>
      </div>
    );
  }

  let places_to_announce_value=0;
  if ( division?.event?.performance_type === "sds" || (division?.event?.performance_type === "twd" && division?.event?.performance_subtype === "showcase") ) {
    places_to_announce_value=division?.places_to_announce || 0;
  } else if ( division?.event?.performance_type === "twd" ) {
    const placesData = JSON.parse(JSON.stringify(division?.twd_places_to_announce || {})) as Record<string, number>;
    const resultsLength = results?.results?.length ?? 0; 
    places_to_announce_value = placesData[resultsLength.toString()] ?? placesData.default ?? 0;
  }

  const isTWDShowcase =
  division?.event?.performance_type === "twd" &&
  division?.event?.performance_subtype === "showcase";

  const isSDSShowcase =
  division?.event?.performance_type === "sds" &&
  division?.event?.performance_subtype === "showcase";

  console.log(currentRole); console.log(division?.status);
  return (
    <div>
      <section className="headersection2">
        <div className="w-layout-blockcontainer container-main-content w-container">
          <div className="div-header-title-section">
            <div
              className="link-block w-inline-block cursor-pointer"
              onClick={() => {
                navigate(`/divisions/${division?.event_id}`);
              }}
            >
              <img src="/images/back-icon.png" loading="lazy" alt="" className="image-2" />
              <h1 className="back-text-link">Back</h1>
            </div>
            <h1 className="judgingapp-heading2">
              {division?.start_time}-{division?.end_time} {division?.title?.split('_')[0]}
            </h1>
            <div className="final-result-div">
              <h1 className="final-result">Final Result - {division?.status}</h1>
              {(isTWDShowcase || isSDSShowcase) ? ( <FinalResultCardHeaderTWDShowcase /> ) : ( <FinalResultCardHeader /> )}
              {(
                ((currentRole === 1 || currentRole === 8) && division?.status !== "judging") ||
                (currentRole === 4 && (division?.status === "ready_to_announce" || division?.status === "announced")) ||
                (currentRole === 7 && division?.status !== "judging") ||
                ((currentRole === 3 || currentRole === 5 || currentRole === 6 || currentRole === 9) && division?.status === "announced")                
              ) ? (

                (isTWDShowcase || isSDSShowcase) ? (
                  (dataFinalResults || []).map((result, index) => (
                    <FinalResultCardTWDShowcase
                      key={`${result.id}-${index}`}
                      result={result}
                      places_to_announce={places_to_announce_value}
                    />
                  ))
                ) : (
                  (dataFinalResults || []).map((result, index) => (
                    <FinalResultCard
                      key={`${result.id}-${index}`}
                      result={result}
                      places_to_announce={places_to_announce_value}
                    />
                  ))
                )
                
                
              ) : (
                <div><br />Calculating Result...{isLoadingfinal_Results && <p>Loading...</p>}</div>
              )}
            </div>
            { (currentRole === 1 || currentRole === 8) && division?.status === "approved" &&  (
              <div className="div-block-16">
              <button
                  className="announce-button w-button"
                  onClick={() => handleReadyToAnnounce()} // Call the parent's handler
                >
                  Ready to Announce
              </button>
              
              </div>
            )}
            { (currentRole === 1 || currentRole === 8) && division?.status === "ready_to_announce" &&  (
              <div className="div-block-16">
              <button className="publish-button w-button" onClick={() => handleResultAnnounced()} >
                Publish Result to Directors
              </button>
              </div>
            )}            
          </div>
        </div>
      </section>

      <section className="section-4" style={{backgroundColor: 'white'}}>
        <div className="w-layout-blockcontainer container-main-content w-container">
          <div>
            <ResultsHeader currentRole={currentRole} approveButtonLabel={approveButtonLabel} isLoaded={hasFetchedfinal_Results} onApproveDisapprove={handleApproveDisapprove} onPlacesToAnnounce={handlePlacesToAnnounce} performanceType={division?.event?.performance_type || ""} performanceSubType={division?.event?.performance_subtype || ""} existingPlacetoannounceCount={division?.places_to_announce || 0}/>
           
            
              {results.results?.map((result: Result, index: number) =>
                (
                  <ResultsCard key={index} currentRole={currentRole} result={result} divisionStatus={division?.status || ""} onIllegalMoveSubmit={handleIllegalMoveSubmit} onRemoveIllegalMovePoints={handleIllegalMoveRemove} removeJudgeScores={removeJudgeScores} performanceAction={performanceAction} closePopup={(closeFn: () => void) => {
                    closePopupRef.current = closeFn;
                  }}
                  />
                )
              )}
            
          </div>
        </div>
      </section>
    </div>
  );
};

export default Placement;